import { Box } from '@mui/material';

interface EnvironmentBadgeProps {
  /** Environment name to display */
  name: string;
  /** Background color (hex) */
  color?: string;
  /** Font size */
  fontSize?: string | number;
  /** Whether badge is clickable */
  clickable?: boolean;
  /** Click handler */
  onClick?: () => void;
}

/**
 * Get text color that contrasts with background color
 * Returns black for light backgrounds, white for dark backgrounds
 */
const getContrastColor = (bgColor: string): string => {
  if (!bgColor || !bgColor.startsWith('#')) return '#000';

  const color = bgColor.replace('#', '');
  const rgb = parseInt(color, 16);

  // Use luminance threshold to determine contrast
  return rgb > 0xffffff / 2 ? '#000' : '#fff';
};

/**
 * Environment badge component with automatic contrast color
 *
 * Used throughout the app to display environment names with their
 * associated colors. Automatically calculates text color for optimal
 * contrast against the background.
 *
 * @example
 * // Basic usage
 * <EnvironmentBadge name="Production" color="#FF0000" />
 *
 * // Clickable
 * <EnvironmentBadge
 *   name="Dev"
 *   color="#00853E"
 *   clickable
 *   onClick={() => navigate(`/env/${envId}`)}
 * />
 *
 * // Custom size
 * <EnvironmentBadge name="Staging" color="#FFA500" fontSize="14px" />
 */
export const EnvironmentBadge = ({
  name,
  color = '#666',
  fontSize = '12px',
  clickable = false,
  onClick,
}: EnvironmentBadgeProps) => {
  return (
    <Box
      component="span"
      onClick={onClick}
      sx={{
        padding: '1px 5px',
        marginRight: '10px',
        fontWeight: 'bold',
        borderRadius: '2px',
        fontSize,
        bgcolor: color,
        color: getContrastColor(color),
        display: 'inline-block',
        cursor: clickable || onClick ? 'pointer' : 'default',
        '&:hover': clickable || onClick ? { opacity: 0.85 } : {},
      }}
    >
      {name}
    </Box>
  );
};
